import { DateTime } from 'luxon';
import { ActionContext } from 'vuex';
import { AddToCartRes, Coupon, FullOrder, HistoryOrder, LineItem, OrderLogistics, OrderTotals } from '@/models/cartModels';
import { addToCart, deleteFromCart, updateCartItem, orderPrice, submitOrder, buildOrderConfirmation, replaceCart, addOrderToCart } from '@/services/orderService';
import { deleteLocalStorage, getLocalStorage, localStorageEnum, replaceLocalStorage, setLocalStorage } from '@/services/localStorageService';
import { Payment } from '@/models/PaymentModels';
import { PaymentIdEnum } from '@/enums/PaymentTypeEnum';
import { errorToast, successToastDetail } from '@/services/pageService';
import selectedLocationModule from '@/store/selectedLocationModule';
import { stringToBool } from '@/services/stringService';
import { useGoogleAnalytics } from '@/composables/useGoogleAnalytics';
import { AccountState } from '@/store/accountModule';
import { ValidateBasketPayload, ValidatedBasketItem } from '@/models/account/rewardModels';
import { validateRewardBasketItems } from '@/services/accountService';
import { BasketValidationDTO, DiscountBasketItemValidationDTO } from '@/models/account/rewardDTOModels';

export interface OrderState {
    deliveryCharge?: number;
    cartItems: LineItem[];
    estimatedTime?: DateTime;
    validCoupons: Coupon[];
    payments: any[];
    tip: number;
    subTotal?: number;
    taxTotal: number;
    grandTotal?: number;
    submittedOrder?: FullOrder;
    orderProcessing: boolean;
    orderError?: string;
    latestMessage: string;
    isLoader: boolean;
    isEditCartLoader: boolean;
    skipPriceAlignment: boolean;
    isCartUpdating: boolean;
    lineItemEdits?: LineItem;
    isModalUpdate: boolean;
    isToppingModalOpen: boolean;
    isLineItemUpdating: boolean;
    appliedRewardDiscount: number;
    rewardItemNameFailures: ReadonlyArray<string>;
    validatedBasketItems: ReadonlyArray<ValidatedBasketItem>;
    isRewardsValidating: boolean;
    rewardsNeedValidation: boolean;
}

const OrderState: OrderState = {
    tip: 0,
    taxTotal: 0,
    cartItems: [],
    validCoupons: getLocalStorage(localStorageEnum.coupons) ?? [],
    payments: [],
    orderProcessing: false,
    latestMessage: '',
    appliedRewardDiscount: 0,
    isLoader: false,
    isEditCartLoader: false,
    skipPriceAlignment: false,
    isCartUpdating: false,
    isToppingModalOpen: false,
    isLineItemUpdating: false,
    isModalUpdate: false,
    rewardItemNameFailures: [],
    validatedBasketItems: [],
    isRewardsValidating: false,
    rewardsNeedValidation: false,
};

export default {
    state: OrderState,
    mutations: {
        SET_IS_LINE_ITEM_UPDATING: (state: OrderState, val: boolean) => {
            state.isLineItemUpdating = val;
        },
        SET_IS_TOPPING_MODAL_OPEN: (state: OrderState, val: boolean) => {
            state.isToppingModalOpen = val;
        },
        SET_IS_MODAL_UPDATE: (state: OrderState, val: boolean) => {
            state.isModalUpdate = val;
        },
        SET_CART_IS_UPDATING: (state: OrderState, val: boolean) => {
            state.isCartUpdating = val;
        },
        SET_SKIP_PRICE_ALIGNMENT: (state: OrderState, val: boolean) => {
            state.skipPriceAlignment = val;
        },
        ADD_CART_ITEM: (state: OrderState, item: LineItem): void => {
            state.cartItems.push(item);
            replaceLocalStorage(localStorageEnum.cart, { lineItems: state.cartItems }, 1);
        },
        REMOVE_CART_ITEM: (state: OrderState, lineItem: LineItem): void => {
            const removeIndex = state.cartItems.findIndex((cart) => cart == lineItem);
            if (removeIndex > -1) {
                state.cartItems.splice(removeIndex, 1);
                replaceLocalStorage(localStorageEnum.cart, { lineItems: state.cartItems }, 1);
            }
        },
        UPDATE_CART_ITEM: (state: OrderState, item: LineItem): void => {
            const removeIndex = state.cartItems.findIndex((cart) => +(cart.lineitemId ?? 0) == +(item.lineitemId ?? 0));
            if (removeIndex || removeIndex == 0) state.cartItems[removeIndex] = item;
            replaceLocalStorage(localStorageEnum.cart, { lineItems: state.cartItems }, 1);
        },
        CLEAR_CART: (state: OrderState): void => {
            state.cartItems = [];
            deleteLocalStorage(localStorageEnum.cart);
            deleteLocalStorage(localStorageEnum.appliedRewards);
        },
        LOGOUT_CART: (state: OrderState): void => {
            state.cartItems = [];
            // logout will now start a fresh session instead of holding onto any cart data
            deleteLocalStorage(localStorageEnum.cart);
            deleteLocalStorage(localStorageEnum.orderLogistics);
            deleteLocalStorage(localStorageEnum.coupons);
            deleteLocalStorage(localStorageEnum.appliedRewards);

            // Old way - replace cart with no user attached
            // state.cartItems.forEach((i) => (i.customerId = ""));
            // replaceLocalStorage(localStorageEnum.cart, { lineItems: state.cartItems }, 1);
        },

        UPDATE_ESTIMATED_TIME: (state: OrderState, date: string) => {
            state.estimatedTime = DateTime.fromSQL(date);
        },
        ADD_COUPON: (state: OrderState, coupon: Coupon) => {
            state.validCoupons.push(coupon);
        },
        REMOVE_COUPON: (state: OrderState, coupon: Coupon) => {
            const { trackRemoveCoupon } = useGoogleAnalytics();
            const removeIndex = state.validCoupons.findIndex((valid) => valid.name === coupon.name);
            if (removeIndex > -1) {
                trackRemoveCoupon(coupon.code);
                state.validCoupons.splice(removeIndex, 1);
            }
        },
        REPLACE_ALL_COUPONS: (state: OrderState, coupons: Coupon[]) => {
            const newCoupons: Coupon[] = [];
            coupons.forEach((apiCoupon) => {
                const oldCoupon = state.validCoupons.find((localCoupon) => localCoupon.phoenixCode === apiCoupon.phoenixCode);
                if (oldCoupon) {
                    // remove coupon from state so it can't get processed twice
                    // single use coupons use the same phoenixCode, this ensures we move on
                    // to the next available singleUseCoupon instead of duplicating the first one found
                    const index = state.validCoupons.indexOf(oldCoupon);
                    if (index > -1) state.validCoupons.splice(index, 1);
                    // Keep .campaign, .campaignCodeId, and .code from user entered coupon
                    // .code gets replaced with phoenixCode by the api but singleUseCodes also uses .code field
                    apiCoupon.campaign = oldCoupon.campaign;
                    apiCoupon.campaignCodeId = oldCoupon.campaignCodeId;
                    apiCoupon.code = oldCoupon.code;
                    newCoupons.push(apiCoupon);
                } else {
                    newCoupons.push(apiCoupon);
                }
            });
            state.validCoupons = newCoupons;
        },
        RESET_COUPONS: (state: OrderState) => {
            state.validCoupons = [];
        },
        UPDATE_TIP: (state: OrderState, tip: number) => (state.tip = tip),
        UPDATE_PRICES: (state: OrderState, fullOrder: FullOrder): void => {
            state.subTotal = +(fullOrder?.orderDetails?.subTotal ?? 0);
            state.taxTotal = +(fullOrder?.orderDetails?.taxTotal ?? 0);
            state.grandTotal = +(fullOrder?.orderDetails?.grandTotal ?? 0);

            // Delivery Charge lives with the selected location not on the order.
            const isDelivery = fullOrder.orderDetails.orderLogistics.orderType?.toLocaleUpperCase() == 'DELIVERY';
            const deliveryCharge: number = isDelivery ? +(selectedLocationModule.state.location?.deliveryCharge ?? 0) : 0.0;
            state.deliveryCharge = deliveryCharge;
        },
        UPDATE_ITEM_PRICES: (state: OrderState, fullOrder: FullOrder): void => {
            const orderItems: any[] = [Object.values(fullOrder.orderDetails.lineItems)].flat();
            const cartItems = state.cartItems;
            // originally based on item.id but miltiple items can have the same id with different customizations
            // this was causing all similar items to have the same price even if they had extra/less toppings
            if (orderItems && cartItems) {
                cartItems.forEach((item, index1) => {
                    orderItems.forEach((orderItem, index2) => {
                        if (index1 == index2) {
                            item.price = orderItem.price;
                        }
                    });
                });
            }
            replaceLocalStorage(localStorageEnum.cart, { lineItems: state.cartItems }, 1);
        },
        ADD_ORDER_PAYMENT: (state: OrderState, payment: Payment) => {
            const newPayments = [...state.payments, payment];
            const giftCardPayments = newPayments.filter((p) => p.paymentType === PaymentIdEnum.giftCard);
            //sort to apply lowest balance gift cards first to ensure maximum utilization of gift cards and reduce leftover balances
            const sortedGiftCards = giftCardPayments.sort((a, b) => +a.balance - +b.balance);
            const otherPayments = newPayments.filter((p) => p.paymentType !== PaymentIdEnum.giftCard);
            state.payments = [...sortedGiftCards, ...otherPayments];
        },
        REMOVE_ORDER_PAYMENT: (state: OrderState, payment: any) => {
            let removeIndex;
            if (payment.paymentType != PaymentIdEnum.giftCard) {
                removeIndex = state.payments.findIndex((p) => p.paymentType == payment.paymentType);
            } else {
                removeIndex = state.payments.findIndex((p) => p.cardNumber === payment.cardNumber);
            }
            state.payments.splice(removeIndex, 1);
        },
        SET_PAYMENT_AMOUNTS: (state: OrderState, fullOrder: FullOrder) => {
            let localGrandTotal = fullOrder.orderDetails.grandTotal ?? 0;
            fullOrder.orderDetails.payments = fullOrder.orderDetails.payments.map((payment) => {
                if (payment.paymentType === PaymentIdEnum.giftCard) {
                    const amountToApply = Math.min(+payment.balance, localGrandTotal);
                    localGrandTotal -= amountToApply;
                    payment.amount = amountToApply;
                } else if (payment.paymentType === PaymentIdEnum.credit) {
                    payment.amount = localGrandTotal;
                }
                return payment;
            });
        },
        UPDATE_ORDER_PAYMENTS: (state: OrderState, grandTotal: number) => {
            if (state.payments.length === 0) return;

            const giftCardPayments = state.payments.filter((p) => p.paymentType === PaymentIdEnum.giftCard);
            const giftcardsTotal = giftCardPayments.reduce((total, payment) => total + +payment.balance, 0);

            // if gift cards cover total, set gift card amounts and remove other payment types
            let indexStart;
            if (giftcardsTotal >= grandTotal) {
                let runningTotal = 0;
                for (let index = 0; index < giftCardPayments.length; index++) {
                    const payment = giftCardPayments[index];
                    runningTotal += +payment.balance;
                    if (runningTotal >= grandTotal && state.payments.length > index + 1) {
                        indexStart = index + 1;
                        break;
                    }
                }
                if (indexStart) {
                    state.payments.splice(indexStart);
                }
            }

            //if grandTotal is 0 or less. remove all payments
            if (grandTotal <= 0) {
                state.payments = [];
            }
        },
        CLEAR_ORDER_PAYMENTS: (state: OrderState) => (state.payments = []),
        SET_ORDER_PROCESSING: (state: OrderState, processing: boolean) => (state.orderProcessing = processing),
        SET_ORDER_ERROR: (state: OrderState, error: string) => (state.orderError = error),
        SUBMITTED_ORDER: (state: OrderState, order: FullOrder) => (state.submittedOrder = new FullOrder(order)),
        UPDATE_LATEST_MESSAGE: (state: OrderState, message: string) => (state.latestMessage = message),
        SET_REWARD_DISCOUNT: (state: OrderState, val: number) => (state.appliedRewardDiscount = val),
        SET_FLAVOR_LOADER: (state: OrderState, val: boolean) => (state.isLoader = val),
        SET_CARTEDIT_LOADER: (state: OrderState, val: boolean) => (state.isEditCartLoader = val),
        SET_BASKET_ITEM_FAILURES: (state: OrderState, failures: ReadonlyArray<DiscountBasketItemValidationDTO>): void => {
            state.rewardItemNameFailures = failures.map((f) => f.discountDetails.name);
        },
        SET_VALIDATED_BASKET_ITEMS: (state: OrderState, items: ReadonlyArray<DiscountBasketItemValidationDTO>): void => {
            state.validatedBasketItems = items.map((item) => {
                return {
                    rewardId: item.discountId,
                    name: item.discountDetails.name,
                    type: item.discountType,
                    description: item.discountDetails.description,
                    discountAmount: item.discountAmount,
                };
            });
            replaceLocalStorage(localStorageEnum.appliedRewards, state.validatedBasketItems, 1);
        },
        SET_REWARDS_VALIDATING: (state: OrderState, val: boolean) => (state.isRewardsValidating = val),
        SET_REWARDS_NEED_VALIDATION: (state: OrderState, val: boolean) => (state.rewardsNeedValidation = val),
    },
    actions: {
        setIsLineItemUpdating(context: ActionContext<OrderState, any>, val: boolean) {
            context.commit('SET_IS_LINE_ITEM_UPDATING', val);
        },
        setIsToppingModalOpen(context: ActionContext<OrderState, any>, val: boolean) {
            context.commit('SET_IS_TOPPING_MODAL_OPEN', val);
        },
        setIsModalUpdate(context: ActionContext<OrderState, any>, val: boolean) {
            context.commit('SET_IS_MODAL_UPDATE', val);
        },
        setSkipPriceAlignment(context: ActionContext<AccountState, any>, val: boolean) {
            context.commit('SET_SKIP_PRICE_ALIGNMENT', val);
        },
        async initCart(context: ActionContext<OrderState, any>, lineItems: LineItem[]): Promise<void> {
            const accountId = context.getters.getAccountId;

            if (lineItems.length > 0 && context.getters.getCartItems?.length == 0) {
                for (const item of lineItems) {
                    item.customerId = accountId;
                    context.commit('ADD_CART_ITEM', new LineItem(item));
                }
                await context.dispatch('alignPrice');
            }
            // use local to replace API cart
            else if (accountId) {
                const fullOrder = await replaceCart(context.getters.getFullOrder);
                context.commit('CLEAR_CART');
                context.commit('UPDATE_PRICES', fullOrder);
                context.commit('UPDATE_ESTIMATED_TIME', fullOrder.orderHeader?.submitResult.promiseTime.date);
                for (const item of fullOrder.orderDetails?.lineItems ?? []) {
                    context.commit('ADD_CART_ITEM', item);
                }
            }
        },
        resetCart(context: ActionContext<OrderState, any>, lineItems: LineItem[]): void {
            const accountId = context.getters.getAccountId;
            context.commit('CLEAR_CART');

            lineItems.forEach((item) => {
                item.customerId = accountId;
                context.commit('ADD_CART_ITEM', new LineItem(item));
            });
            replaceLocalStorage(localStorageEnum.cart, context.getters.getCartItems, 1);
            context.dispatch('alignPrice');
        },
        addDonation(context: ActionContext<OrderState, any>, item: LineItem): void {
            const cartDonation = context.getters.getCartDonation;
            if (cartDonation) {
                item.lineitemId = cartDonation.lineitemId;
                context.dispatch('updateCartItem', item);
            } else {
                context.dispatch('addCartItem', item);
            }
        },
        removeDonation(context: ActionContext<OrderState, any>): void {
            const cartDonation = context.getters.getCartDonation;
            if (cartDonation) {
                context.dispatch('deleteCartItem', cartDonation);
            }
        },
        async addCartItem(context: ActionContext<OrderState & AccountState, any>, item: LineItem): Promise<LineItem[]> {
            const { trackAddItem } = useGoogleAnalytics();
            context.commit('ADD_CART_ITEM', item);
            trackAddItem(item.name);
            try {
                context.commit('SET_CART_IS_UPDATING', true);
                const accountId = context.getters.getAccountId;
                if (accountId) {
                    context.dispatch('validateBasketItems');
                    item.customerId = accountId;
                    const res = await addToCart(item);
                    item.lineitemId = res.lineItems[res.lineItems.length - 1].lineitemId;
                    context.commit('UPDATE_ESTIMATED_TIME', res.promiseTime);
                } else {
                    item.lineitemId = context.getters.getCartItems.length > 0 ? Math.max(...context.getters.getCartItems.map((item) => item.lineitemId ?? 0)) + 1 : 0;
                }
                await context.dispatch('alignPrice');

                return context.getters.getCartItems;
            } catch (error) {
                throw errorToast(error as string);
            } finally {
                context.commit('SET_CART_IS_UPDATING', false);
            }
        },

        addOrderToCart(context: ActionContext<OrderState, any>, order: HistoryOrder): Promise<void> {
            const { trackAddItem } = useGoogleAnalytics();

            return new Promise((resolve, reject) => {
                const accountId = context.getters.getAccountId;
                if (accountId) {
                    addOrderToCart(order.orderHeaderId, accountId)
                        .then(
                            (res: AddToCartRes) => {
                                context.commit('CLEAR_CART'); // clear cart out, then replace with api response
                                //
                                // TODO: Force users to select a location if one is not already and they are attempting to re-order
                                res.lineItems.forEach((item) => {
                                    context.commit('ADD_CART_ITEM', new LineItem(item));
                                    trackAddItem(item.name);
                                });

                                context.commit('UPDATE_ESTIMATED_TIME', res.promiseTime);
                                context.dispatch('alignPrice');
                                resolve(context.getters.getCartItems);
                            },
                            (error) => reject(errorToast(error))
                        )
                        .catch((error) => reject(errorToast(error)));
                }
            });
        },
        async updateCartItem(context: ActionContext<OrderState, any>, item: LineItem): Promise<LineItem[]> {
            const accountId = context.getters.getAccountId;
            context.commit('UPDATE_CART_ITEM', item);
            if (accountId) {
                context.commit('SET_CART_IS_UPDATING', true);
                context.dispatch('validateBasketItems');
                item.customerId = accountId;
                updateCartItem(item)
                    .then(async (res) => {
                        context.commit('UPDATE_ESTIMATED_TIME', res.promiseTime);
                        await context.dispatch('alignPrice');
                    })
                    .catch((error) => {
                        console.log('Error: ' + error);
                    })
                    .finally(() => {
                        context.commit('SET_CART_IS_UPDATING', false);
                    });
            } else {
                await context.dispatch('alignPrice');
            }
            return context.getters.getCartItems;
        },

        deleteCartItem(context: ActionContext<OrderState & AccountState, any>, item: LineItem): Promise<void> {
            const { trackRemoveItem } = useGoogleAnalytics();
            return new Promise((resolve, reject) => {
                const accountId = context.getters.getAccountId;
                const deletePayload = { customerId: accountId, lineitemId: item.lineitemId };
                trackRemoveItem(item.name);
                context.commit('REMOVE_CART_ITEM', item);
                context.dispatch('removeDonationIfOnlyItem');
                context.dispatch('validateBasketItems');
                if (accountId) {
                    context.commit('SET_CART_IS_UPDATING', true);
                    deleteFromCart(deletePayload)
                        .then(async (res) => {
                            context.commit('UPDATE_ESTIMATED_TIME', res.promiseTime);
                            context.dispatch('alignPrice');
                            resolve();
                        })
                        .catch((error) => reject(errorToast(error)))
                        .finally(() => context.commit('SET_CART_IS_UPDATING', false));
                } else {
                    context.dispatch('alignPrice');
                    resolve();
                }
            });
        },
        removeDonationIfOnlyItem(context: ActionContext<OrderState, any>) {
            const cartItems = context.getters.getCartItems;
            if (cartItems.length == 1 && cartItems[0].isDonation) {
                context.dispatch('removeDonation');
            }
        },
        updateTip(context: ActionContext<OrderState, any>, tip: number): void {
            context.commit('UPDATE_TIP', tip);
            replaceLocalStorage(localStorageEnum.grandTotal, context.getters.getGrandTotal, 0.04); // Dynatrace Data
        },
        alignPrice(context: ActionContext<OrderState, any>): Promise<any> | undefined {
            if (context.getters.getSkipPriceAlignment) {
                return;
            }
            if (context.getters.getCartQuantity == 0) {
                return;
            }

            return orderPrice(context.getters.getFullOrder)
                .then((res) => {
                    if (res && !Array.isArray(res) && typeof res === 'object') {
                        const order = new FullOrder(res);
                        context.commit('UPDATE_PRICES', order);
                        context.commit('UPDATE_ESTIMATED_TIME', order.orderHeader?.submitResult.promiseTime.date);
                        context.commit('UPDATE_ITEM_PRICES', order);
                        context.dispatch('updateLatestMessage', order);
                        context.dispatch('realignCoupons', order);
                        replaceLocalStorage(localStorageEnum.grandTotal, context.getters.getGrandTotal, 0.04); // Dynatrace Data
                    } else {
                        if (res != null) {
                            throw 'Error: ' + res;
                        }
                    }
                })
                .catch((error) => {
                    console.log('Error: ' + error);
                })
                .finally(() => {
                    replaceLocalStorage(localStorageEnum.itemsInCart, context.getters.getCartQuantity, 0.04);
                });
        },
        addOrderPayment(context: ActionContext<OrderState, any>, payment: any): void {
            const allPayments = context.state.payments;
            const isMatchingPayment = allPayments.some((p) => payment.paymentType === PaymentIdEnum.giftCard && p.cardNumber === payment.cardNumber);
            if (isMatchingPayment) return;

            context.commit('ADD_ORDER_PAYMENT', payment);
        },
        updateOrderPayments(context: ActionContext<OrderState, any>): void {
            const grandTotal = context.getters.getGrandTotal;
            context.commit('UPDATE_ORDER_PAYMENTS', grandTotal);
        },
        clearOrderPayments(context: ActionContext<OrderState, any>) {
            context.commit('CLEAR_ORDER_PAYMENTS');
        },
        removeOrderPayment(context: ActionContext<OrderState, any>, payment: Payment): void {
            context.commit('REMOVE_ORDER_PAYMENT', payment);
        },
        updateEstimatedTime(context: ActionContext<OrderState, any>, date: string): void {
            context.commit('UPDATE_ESTIMATED_TIME', date);
        },
        updateLatestMessage(context: ActionContext<OrderState, any>, order: FullOrder): void {
            if (!['/cart', '/checkout'].includes(window.location.pathname)) return;
            //@ts-ignore
            const newMessage = order.orderHeader?.submitResult?.messages[0];
            if (newMessage && newMessage !== context.state.latestMessage) errorToast(newMessage);
            context.commit('UPDATE_LATEST_MESSAGE', newMessage);
        },
        realignCoupons(context: ActionContext<OrderState, any>, order: FullOrder): void {
            context.commit('REPLACE_ALL_COUPONS', order.orderDetails.coupons);
        },
        setOrderProcessing(context: ActionContext<OrderState, any>, status: boolean): void {
            context.commit('SET_ORDER_PROCESSING', status);
        },
        submitOrder(context: ActionContext<OrderState & AccountState, any>, requestOrder: FullOrder): Promise<any> {
            context.commit('SET_ORDER_PROCESSING', true);
            context.commit('SET_ORDER_ERROR');
            context.commit('SET_PAYMENT_AMOUNTS', requestOrder);
            const { trackOrderPlacement, trackOrderPlacementError, trackProductPurchase, trackCouponPurchase } = useGoogleAnalytics();
            // Reset Time if asap order
            const orderDetail: OrderLogistics = requestOrder.orderDetails.orderLogistics;
            if (stringToBool(orderDetail.isImmediate)) {
                orderDetail.time = DateTime.now().toLocaleString(DateTime.TIME_24_SIMPLE);
                // set payload data:
                requestOrder.orderDetails.orderLogistics.time = orderDetail.time;
                // set store data:
                context.dispatch('initStoreLocation', orderDetail ? new OrderLogistics(orderDetail) : undefined);
            } // END reset time if asap order

            return new Promise((resolve, reject) => {
                submitOrder(requestOrder)
                    .then((res) => {
                        // fetchOrderDetail(res.orderHeaderID)
                        //   .then((responseOrder) => {
                        // todo fetchOrderDetail should be returning all of this.. but for now, rebuild from memory
                        const confirmation = buildOrderConfirmation(requestOrder, res);
                        const appliedRewards = getLocalStorage(localStorageEnum.appliedRewards);
                        if (appliedRewards) {
                            confirmation.orderDetails.submittedRewardItems = appliedRewards;
                        }
                        context.commit('SUBMITTED_ORDER', confirmation);
                        context.commit('CLEAR_CART');
                        const storedOrders = getLocalStorage(localStorageEnum.confirmation) ?? [];
                        setLocalStorage(localStorageEnum.confirmation, [...storedOrders, confirmation], 1);
                        resolve(confirmation);

                        confirmation.orderDetails.lineItems.forEach((item) => trackProductPurchase(item.id, item.displayName, item.price, item.quantity));
                        confirmation.orderDetails.coupons.forEach((coupon) => trackCouponPurchase(coupon.code, coupon.name));
                        trackOrderPlacement(confirmation.orderHeaderID, confirmation.orderDetails.taxTotal, confirmation.orderDetails.grandTotal);

                        if (context.getters.isLoggedIn) {
                            successToastDetail({
                                message: `<h1 class='mt-0'>We're on it!</h1><img src="${require('@/assets/pep/successPep.png')}"/><br/>Points arrive after you get your food.<br/> <small>May take up to 24 hours.</small>`,
                                position: 'middle',
                                color: 'light',
                                duration: 3000,
                                cssClass: 'rewards-toast',
                            });
                        }
                        if (context.getters.getAccount?.customerId) context.commit('SET_ORDER_HISTORY');
                    })
                    // })
                    .catch((err) => {
                        trackOrderPlacementError(err);
                        reject(context.commit('SET_ORDER_ERROR', err));
                    })
                    .finally(() => context.commit('SET_ORDER_PROCESSING', false));
            });
        },
        // todo use fetchOrderDetail
        fetchSubmittedOrder(context: ActionContext<OrderState, any>, id: string) {
            const foundOrder = getLocalStorage(localStorageEnum.confirmation)?.find((o) => o.orderHeaderID == id);
            context.commit('SUBMITTED_ORDER', foundOrder);
        },
        addCoupon(context: ActionContext<OrderState, any>, coupon: Coupon) {
            context.commit('ADD_COUPON', coupon);
            const isLoggedIn = context.getters.isLoggedIn;
            if (isLoggedIn) {
                context.dispatch('validateBasketItems');
            }
            return context.dispatch('alignPrice');
        },
        removeCoupon(context: ActionContext<OrderState, any>, coupon: Coupon) {
            context.commit('REMOVE_COUPON', coupon);
            const isLoggedIn = context.getters.isLoggedIn;
            if (isLoggedIn) {
                context.dispatch('validateBasketItems');
            }
            return context.dispatch('alignPrice');
        },
        resetCoupons(context: ActionContext<OrderState, any>, alignPrice = false) {
            context.commit('RESET_COUPONS');
            if (alignPrice) return context.dispatch('alignPrice');
            return null;
        },
        removeOrderError(context: ActionContext<OrderState, any>) {
            context.commit('SET_ORDER_ERROR', undefined);
        },
        clearCart(context: ActionContext<OrderState, any>) {
            context.commit('CLEAR_CART');
        },
        logoutCart(context: ActionContext<OrderState, any>) {
            context.commit('LOGOUT_CART');
        },
        flavorLoader(context: ActionContext<OrderState, any>, val: boolean) {
            context.commit('SET_FLAVOR_LOADER', val);
        },
        cartEditLoader(context: ActionContext<OrderState, any>, val: boolean) {
            context.commit('SET_CARTEDIT_LOADER', val);
        },
        setRewardsValidating(context: ActionContext<OrderState, any>, val: boolean) {
            context.commit('SET_REWARDS_VALIDATING', val);
        },
        async validateBasketItems(context: ActionContext<OrderState & AccountState, any>): Promise<void> {
            const cartItems = context.getters.getCartItems;
            const rewardsBasketInfo = context.getters.getRewardsBasketInfo;
            const subTotal = context.getters.getSubTotalNoDonation ?? 0;
            const subTotalMinusDiscounts = subTotal - context.getters.getCouponsDiscount;

            if (!rewardsBasketInfo || rewardsBasketInfo.basketItems.length === 0 || cartItems.length === 0 || !subTotalMinusDiscounts) {
                context.commit('SET_REWARDS_VALIDATING', false);
                return;
            }

            const itemId = cartItems[0].id;
            const storeNumber = itemId.split(',')[0].split('=')[1];

            context.commit('SET_REWARDS_NEED_VALIDATION', true);
            try {
                const validateBasketPayload: ValidateBasketPayload = {
                    storeNumber: storeNumber,
                    lineItems: cartItems,
                    receiptDatetime: new Date().toISOString(),
                    subtotalAmount: subTotalMinusDiscounts,
                    receiptAmount: subTotalMinusDiscounts,
                };
                const response: BasketValidationDTO = await validateRewardBasketItems(validateBasketPayload);
                if (response) {
                    const discounts = response.success.map((success) => success.discountAmount);
                    const totalRewardDiscount = discounts.reduce((acc: number, curr: any) => acc + curr, 0);
                    context.commit('SET_REWARD_DISCOUNT', totalRewardDiscount);
                    context.commit('SET_BASKET_ITEM_FAILURES', response.failures);
                    context.commit('SET_VALIDATED_BASKET_ITEMS', response.success);
                }
            } catch (error) {
                console.log('Error validating rewards: ' + error);
                errorToast('Unable to process rewards at this time.');
            } finally {
                context.commit('SET_REWARDS_VALIDATING', false);
                context.commit('SET_REWARDS_NEED_VALIDATION', false);
            }
        },
    },
    getters: {
        getIsLineItemUpdating: (state: OrderState): boolean => state.isLineItemUpdating,
        getIsToppingModalOpen: (state: OrderState): boolean => state.isToppingModalOpen,
        getIsModalUpdate: (state: OrderState): boolean => state.isModalUpdate,
        getCouponsDiscount: (state: OrderState): number => {
            // @ts-ignore
            const discountArray = state.validCoupons.map((coupon) => parseFloat(coupon.discount));
            if (discountArray.length > 0) {
                const total = discountArray.reduce((partialSum, a) => (partialSum ?? 0) + (a ?? 0), 0) ?? 0.0;
                return total;
            }
            return 0.0;
        },
        getDeliveryCharge: (state: OrderState): number => +(state.deliveryCharge ?? 0).toFixed(2),
        getCartItems: (state: OrderState): LineItem[] => state.cartItems,
        getCartItemsSorted: (state: OrderState): LineItem[] =>
            [...state.cartItems]
                .filter((i) => {
                    if (!i.bundleIdentifier) {
                        return true;
                    } else {
                        return i.bundleIdentifier.startsWith('vm');
                    }
                })
                .reverse()
                .sort((a, b) => a.lineitemId ?? 0 - (b?.lineitemId ?? 0)),
        getCartBundleItemsSorted: (state: OrderState): LineItem[] => {
            const items = [...state.cartItems].filter((i) => i.bundleIdentifier && !i.bundleIdentifier.startsWith('vm'));
            const groupBy = function (xs, key) {
                return xs.reduce(function (rv, x) {
                    const val = x[key].split('--')[0];
                    (rv[val] = rv[val] || []).push(x);
                    return rv;
                }, {});
            };

            return groupBy(items, 'bundleIdentifier');
        },
        getCartItemsNoDonation: (state: OrderState): LineItem[] => state.cartItems.filter((item) => !item.isDonation).sort((a, b) => a.lineitemId ?? 0 - (b?.lineitemId ?? 0)),
        getCartDonation: (state: OrderState, getters: any): LineItem => getters.getCartItems.find((item) => item.isDonation),
        getIsCartUpdating: (state: OrderState): boolean => state.isCartUpdating,
        getCartItemsNameSort: (state: OrderState): LineItem[] => state.cartItems.sort((a, b) => a.name.localeCompare(b.name)),
        getCartQuantity: (state: OrderState): number => state.cartItems.reduce((t, item) => t + +item.quantity, 0),
        getSubTotal: (state: OrderState, getters: any): number => +getters.getCartItems.reduce((prev, curr) => prev + curr.quantity * curr.price, 0).toFixed(2),
        getSubTotalPlusTip: (state: OrderState, getters: any): number =>
            (+getters.getCartItems.reduce((prev, curr) => prev + curr.quantity * curr.price, 0) + getters.getTip + getters.getTax + getters.getDeliveryCharge).toFixed(2),
        getSubTotalNoDonation: (state: OrderState, getters: any): number => +getters.getCartItemsNoDonation.reduce((prev, curr) => prev + curr.quantity * curr.price, 0).toFixed(2),
        getDonationAmount: (state: OrderState): number => state.cartItems.find((c) => c.isDonation)?.price ?? 0,
        getTip: (state: OrderState): number => state.tip,
        getTax: (state: OrderState): number => state.taxTotal,
        getGrandTotal: (state: OrderState, getters: any): number => +getters.getSubTotalPlusTip - getters.getCouponsDiscount - getters.getAppliedRewardDiscount,
        getGrandTotalNoDonation: (state: OrderState, getters: any): number => getters.getSubTotalNoDonation + state.tip + state.taxTotal + getters.getDeliveryCharge - getters.getCouponsDiscount,
        getOrderTotals: (state: OrderState, getters: any): OrderTotals => {
            return new OrderTotals({
                tip: getters.getTip,
                tax: getters.getTax,
                cartTotal: getters.getSubTotal,
                donation: getters.getDonationAmount,
                deliveryCharge: state.deliveryCharge,
                grandTotal: getters.getGrandTotal,
            });
        },
        getEstimatedTime: (state: OrderState): DateTime | undefined => state.estimatedTime,
        getValidCoupons: (state: OrderState): Coupon[] => state.validCoupons,
        getPayments: (state: OrderState): Payment[] => state.payments,
        getPaymentCoversTotal: (state: OrderState, getters: any): boolean => {
            if (state.payments.length === 0) {
                return getters.getGrandTotal === 0;
            }

            const isCashOrCreditPayment = state.payments.some((p) => p.paymentType != PaymentIdEnum.giftCard);
            if (isCashOrCreditPayment) return true;

            const giftCardTotal = state.payments.filter((p) => p.paymentType === PaymentIdEnum.giftCard)?.reduce((total, payment) => total + +payment.balance, 0);
            if (giftCardTotal >= getters.getGrandTotal) return true;

            return false;
        },
        getOrderProcessing: (state: OrderState): boolean => state.orderProcessing,
        getOrderError: (state: OrderState): string | undefined => state.orderError,
        getSubmittedOrder: (state: OrderState): FullOrder | undefined => state.submittedOrder,
        getPrimaryPayment: (state: OrderState): Payment | undefined => state.payments[0],
        getSecondaryPayment: (state: OrderState): Payment | undefined => state.payments[1],
        getPaymentType: (state: OrderState): PaymentIdEnum | undefined => {
            if (state.payments.length > 1) {
                return PaymentIdEnum.giftCard;
            } else if (state.payments.length == 1) {
                return state.payments[0].paymentType;
            } else {
                return undefined;
            }
        },
        getLatestMessage: (state: OrderState): string => state.latestMessage,
        getFlavorLoader: (state: OrderState): boolean => state.isLoader,
        getCartEditLoader: (state: OrderState): boolean => state.isEditCartLoader,
        getSkipPriceAlignment: (state: OrderState): boolean => state.skipPriceAlignment,
        getAppliedRewardDiscount: (state: OrderState): number => state.appliedRewardDiscount,
        getRewardItemNameFailures: (state: OrderState): ReadonlyArray<string> => state.rewardItemNameFailures,
        getValidatedBasketItems: (state: OrderState): ReadonlyArray<ValidatedBasketItem> => state.validatedBasketItems,
        getIsRewardsValidating: (state: OrderState): boolean => state.isRewardsValidating,
        getRewardsNeedValidation: (state: OrderState): boolean => state.rewardsNeedValidation,
    },
};
